import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard, SectionLabel } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, Scissors, Hammer, Leaf } from "lucide-react";

const HERO = "https://customer-assets-rejwkqb3.emergentagent.net/job_luxa-store/artifacts/dnlfj5mm_mian%20page%20.png";
const CRAFT = "https://customer-assets-rejwkqb3.emergentagent.net/job_luxa-store/artifacts/a2u7hzkx_Man%20Belt.png";
const STITCH = "https://customer-assets-rejwkqb3.emergentagent.net/job_luxa-store/artifacts/aiyp74c4_Luxa%20Business%20man%20hand%20bag.png";

const TESTIMONIALS = [
  { q: "Six months of daily carry and the Meridian looks better than the day it arrived.", a: "Daniel R.", c: "London" },
  { q: "The only belt I've owned where the buckle feels as considered as the leather.", a: "Elena K.", c: "Chicago" },
  { q: "Bought as a gift, ended up ordering a second one for myself the same week.", a: "Marcus T.", c: "Singapore" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
    api.get("/blog").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <section className="bg-luxa-espresso" data-testid="hero-banner">
        <img src={HERO} alt="Trust Luxa — luxury is in the details"
          className="w-full h-auto block animate-fade-up" />
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-12 flex flex-wrap items-center gap-5">
          <Button asChild className="rounded-none bg-luxa-cream hover:bg-luxa-gold text-luxa-espresso h-14 px-9 overline">
            <Link to="/shop" data-testid="hero-shop-button">Explore the Collection</Link>
          </Button>
          <Button asChild variant="outline"
            className="rounded-none border-luxa-cream/35 bg-transparent text-luxa-cream hover:bg-luxa-cream hover:text-luxa-espresso h-14 px-9 overline">
            <Link to="/craftsmanship" data-testid="hero-craft-button">Our Craft</Link>
          </Button>
          <p className="ml-auto overline text-luxa-cream/50">100% leather · Ten-year repair promise</p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 lg:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <SectionLabel>The Selection</SectionLabel>
            <h2 className="mt-5 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight">Pieces we make again and again</h2>
          </div>
          <Link to="/shop" className="overline text-luxa-brown flex items-center gap-2 link-underline" data-testid="view-all-products">
            View all <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-14">
          {featured.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
        </div>
      </section>

      <section className="bg-luxa-espresso text-luxa-cream">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[420px] overflow-hidden group bg-luxa-espresso">
            <img src={CRAFT} alt="Trust Luxa belt detail" className="img-zoom w-full h-full object-cover" />
          </div>
          <div className="px-5 sm:px-12 lg:px-16 py-20 flex flex-col justify-center">
            <p className="overline text-luxa-gold">Since the first hide</p>
            <h2 className="mt-6 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight">
              We chose the slower way,<br />and never found a reason<br />to change it.
            </h2>
            <p className="mt-7 text-luxa-cream/65 leading-relaxed max-w-lg">
              Trust Luxa is built on trust, durability and elegance — traditional leather craftsmanship
              held to a modern luxury standard. Every piece starts the same way: one full-grain panel,
              chosen for its grain and tanned with tree bark rather than chrome. What follows is patience —
              bevelled edges, hand-slanted awl holes, three passes of burnishing — and an object that
              improves with every year you carry it.
            </p>
            <div className="mt-12 grid sm:grid-cols-3 gap-8">
              {[[Leaf, "Vegetable tanned", "Bark-tanned hides, no chrome salts."],
                [Scissors, "Hand cut", "Single-panel construction, no filler."],
                [Hammer, "Saddle stitched", "Two needles, one waxed thread."]].map(([Icon, t, d]) => (
                <div key={t}>
                  <Icon size={22} strokeWidth={1.5} className="text-luxa-gold" />
                  <p className="mt-4 font-serif text-lg">{t}</p>
                  <p className="mt-2 text-sm text-luxa-cream/55 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 lg:py-32">
        <SectionLabel>In their words</SectionLabel>
        <div className="mt-14 grid md:grid-cols-3 gap-10 lg:gap-16">
          {TESTIMONIALS.map((t) => (
            <blockquote key={t.a} className="border-t border-luxa-espresso/15 pt-7">
              <p className="font-serif text-xl lg:text-2xl leading-snug text-luxa-espresso/90">“{t.q}”</p>
              <footer className="mt-6 overline text-luxa-espresso/45">{t.a} — {t.c}</footer>
            </blockquote>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 pb-8">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
          <div className="relative overflow-hidden group aspect-[16/11] bg-luxa-espresso">
            <img src={STITCH} alt="Trust Luxa briefcase" className="img-zoom w-full h-full object-cover" />
          </div>
          <div>
            <SectionLabel>The Journal</SectionLabel>
            <h2 className="mt-5 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight">Care, style and the making of things</h2>
            <div className="mt-10 divide-y divide-luxa-espresso/10">
              {posts.map((p) => (
                <Link key={p.id} to={`/journal/${p.slug}`} data-testid={`home-post-${p.slug}`} className="block py-6 group">
                  <h3 className="font-serif text-xl group-hover:text-luxa-gold transition-colors duration-300">{p.title}</h3>
                  <p className="mt-2 text-sm text-luxa-espresso/60 leading-relaxed">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20">
        <div className="border border-luxa-sand px-6 sm:px-14 py-14 text-center bg-luxa-shell">
          <p className="overline text-luxa-brown">Complimentary shipping on every order</p>
          <h2 className="mt-5 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight">Buy once. Repair, never replace.</h2>
          <p className="mt-5 text-luxa-espresso/60 max-w-xl mx-auto">
            Every Trust Luxa piece carries a ten-year repair promise. Send it back, we restitch it.
          </p>
          <Button asChild className="mt-9 rounded-none bg-luxa-espresso hover:bg-luxa-espresso/85 text-luxa-cream h-13 px-10 overline py-4">
            <Link to="/shop" data-testid="cta-shop-button">Shop the Collection</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
